import {
  BillingDetailType,
  BillingStatus,
  CompanyStatus,
  CouponDiscountType,
  CouponStatus,
  FeatureStatus,
  FeatureType,
  OptionContractStatus,
  OptionStatus,
  PlanContractStatus,
  PlanStatus,
  ServiceStatus,
  ServiceType,
} from "@/generated/graphql_billing";

import {
  addDays,
  addMonths,
  format,
  lastDayOfMonth,
  subDays,
  subMonths,
} from "date-fns";

const bizBillingBirthday = new Date("2023-05-31");
const today = new Date("2024-06-30");
const monthAfterToday = addMonths(today, 1);
const monthBeforeToday = subMonths(today, 1);
const twoMonthBeforeToday = subMonths(today, 2);

const monthsBeforeToday = (months: number) => subMonths(today, months);
const yearMonthBeforeToday = (months: number) =>
  format(monthsBeforeToday(months), "yyyyMM");

const feature = {
  CFMCashFlowForecast: {
    id: "1",
    name: "入出金予定",
    type: FeatureType.Mikatano,
    code: "CFM_CASH_FLOW_FORECAST",
    status: FeatureStatus.Active,
    displayOrder: 1,
  },
  CFMCashFlowStatement: {
    id: "2",
    name: "資金繰り表",
    type: FeatureType.Mikatano,
    code: "CFM_CASH_FLOW_STATEMENT",
    status: FeatureStatus.Active,
    displayOrder: 2,
  },
  CFMTransactionMatching: {
    id: "9",
    name: "消込",
    type: FeatureType.Mikatano,
    code: "CFM_TRANSACTION_MATCHING",
    status: FeatureStatus.Active,
    displayOrder: 3,
  },
  InvoicePayByCard: {
    id: "3",
    name: "請求書カード払い",
    type: FeatureType.Mikatano,
    code: "INVOICE_PAY_BY_CARD",
    status: FeatureStatus.Active,
    displayOrder: 100,
  },
  TestFeature1: {
    id: "4",
    name: "TestFeature1",
    type: FeatureType.Mikatano,
    code: "TEST_FEATURE_1",
    status: FeatureStatus.Active,
    displayOrder: 200,
  },
  TestFeature2: {
    id: "5",
    name: "テストフィーチャー2",
    type: FeatureType.Mikatano,
    code: "TEST_FEATURE_2",
    status: FeatureStatus.Active,
    displayOrder: 201,
  },
  TestFeature3: {
    id: "6",
    name: "テストFeature3",
    type: FeatureType.Mikatano,
    code: "TEST_FEATURE_3",
    status: FeatureStatus.Active,
    displayOrder: 202,
  },
  TestFeature4: {
    id: "7",
    name: "Testフィーチャー4",
    type: FeatureType.Mikatano,
    code: "TEST_FEATURE_4",
    status: FeatureStatus.Active,
    displayOrder: 203,
  },
  TestFeature5: {
    id: "8",
    name: "テストフィーチャー5長い",
    type: FeatureType.Mikatano,
    code: "TEST_FEATURE_5",
    status: FeatureStatus.Active,
    displayOrder: 204,
  },
};

const service = {
  DXF: {
    id: "1",
    name: "Mikatano ワークス",
    type: ServiceType.Mikatano,
    code: "DXF",
    status: ServiceStatus.Active,
    displayOrder: 0,
    allFeatures: [],
    planFeatures: [],
  },
  BFM: {
    id: "2",
    name: "Mikatano 資金管理",
    type: ServiceType.Mikatano,
    code: "BFM",
    status: ServiceStatus.Active,
    displayOrder: 1,
    allFeatures: [
      feature.CFMCashFlowForecast,
      feature.CFMCashFlowStatement,
      feature.CFMTransactionMatching,
    ],
    planFeatures: [],
  },
  Invoice: {
    id: "3",
    name: "Mikatano インボイス管理",
    type: ServiceType.Mikatano,
    code: "INVOICE",
    status: ServiceStatus.Active,
    displayOrder: 2,
    allFeatures: [feature.InvoicePayByCard],
    planFeatures: [],
  },
  MFCInvoice: {
    id: "4",
    name: "マネーフォワード クラウド請求書",
    type: ServiceType.Other,
    code: "MFC_INVOICE",
    status: ServiceStatus.Active,
    displayOrder: 3,
    allFeatures: [],
    planFeatures: [],
  },
  Loan: {
    id: "5",
    name: "Mikatano 融資ポータル",
    type: ServiceType.Mikatano,
    code: "LOAN",
    status: ServiceStatus.Active,
    displayOrder: 4,
    allFeatures: [],
    planFeatures: [],
  },
  ExternalTroubleSupport: {
    id: "6",
    name: "トラブルサポートデスク",
    type: ServiceType.Other,
    code: "EXTERNAL_TROUBLESUPPORT",
    status: ServiceStatus.Active,
    displayOrder: 5,
    allFeatures: [],
    planFeatures: [],
  },
  ExternalBusinessSupport: {
    id: "7",
    name: "ビジネスサポートデスク",
    type: ServiceType.Other,
    code: "EXTERNAL_BUSINESSSUPPORT",
    status: ServiceStatus.Active,
    displayOrder: 6,
    allFeatures: [],
    planFeatures: [],
  },
  ExternalBenefitSelect: {
    id: "8",
    name: "ベネフィットセレクト",
    type: ServiceType.Other,
    code: "EXTERNAL_BENEFITSELECT",
    status: ServiceStatus.Active,
    displayOrder: 7,
    allFeatures: [],
    planFeatures: [],
  },
  FeatureTest: {
    id: "9",
    name: "FeatureTestService",
    type: ServiceType.Mikatano,
    code: "TEST_FEATURE",
    status: ServiceStatus.Active,
    displayOrder: 8,
    allFeatures: [
      feature.TestFeature1,
      feature.TestFeature2,
      feature.TestFeature3,
      feature.TestFeature4,
      feature.TestFeature5,
    ],
    planFeatures: [],
  },
  BFMWithNoFeatures: {
    id: "10",
    name: "Mikatano 資金管理",
    type: ServiceType.Mikatano,
    code: "BFM",
    status: ServiceStatus.Active,
    displayOrder: 9,
    allFeatures: [],
    planFeatures: [],
  },
  InvoiceWithNoFeatures: {
    id: "11",
    name: "Mikatano インボイス管理",
    type: ServiceType.Mikatano,
    code: "INVOICE",
    status: ServiceStatus.Active,
    displayOrder: 10,
    allFeatures: [],
    planFeatures: [],
  },
};

const allServices = [
  service.DXF,
  service.BFM,
  service.Invoice,
  service.MFCInvoice,
  service.Loan,
  service.ExternalTroubleSupport,
  service.ExternalBusinessSupport,
  service.ExternalBenefitSelect,
];

const mikatanoServices = [service.DXF, service.BFM, service.Invoice];

const allServicesNoFeatures = [
  service.DXF,
  service.BFMWithNoFeatures,
  service.InvoiceWithNoFeatures,
  service.MFCInvoice,
  service.Loan,
  service.ExternalTroubleSupport,
  service.ExternalBusinessSupport,
  service.ExternalBenefitSelect,
];

const mikatanoServicesNoFeatures = [
  service.DXF,
  service.BFMWithNoFeatures,
  service.InvoiceWithNoFeatures,
];

const edgeCase = [service.DXF, service.BFM, service.FeatureTest];

const bankServices = {
  withFeatures: allServices,
  withoutFeatures: allServicesNoFeatures,
  mikatanoServicesWithFeatures: mikatanoServices,
  mikatanoServicesWithoutFeatures: mikatanoServicesNoFeatures,
  edgeCase: edgeCase,
};

const plan = {
  DX: {
    id: "1",
    bankCode: "9999",
    name: "まとめてDX化プラン",
    price: 7500,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "まとめてDX化プランの説明",
    displayOrder: 1,
    priceIncludingTax: 7500 * 1.1,
    services: [service.DXF, service.BFM, service.Invoice],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Accounting: {
    id: "2",
    bankCode: "9999",
    name: "経理業務おまかせプラン",
    price: 3000,
    priceIncludingTax: 3000 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "経理業務おまかせプランの説明",
    displayOrder: 2,
    services: [service.BFM, service.Invoice],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Works: {
    id: "3",
    bankCode: "9999",
    name: "ワークスプラン",
    price: 4500,
    priceIncludingTax: 4500 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "ワークスプランの説明",
    displayOrder: 3,
    services: [service.DXF, service.BFM],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  BFM: {
    id: "4",
    bankCode: "9999",
    name: "資金管理プラン",
    price: 0,
    priceIncludingTax: 0 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "",
    displayOrder: 4,
    services: [service.BFM],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Draft: {
    id: "5",
    bankCode: "9999",
    name: "リリース前のプラン",
    price: 0,
    priceIncludingTax: 0 * 1.1,
    status: PlanStatus.Draft,
    publishedAtFrom: monthBeforeToday,
    publishedAtTo: monthAfterToday,
    description: "",
    displayOrder: 5,
    services: [service.BFM],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Inactive: {
    id: "6",
    bankCode: "9999",
    name: "リリース終了のプラン",
    price: 0,
    priceIncludingTax: 0 * 1.1,
    status: PlanStatus.Inactive,
    publishedAtFrom: twoMonthBeforeToday,
    publishedAtTo: monthBeforeToday,
    description: "",
    displayOrder: 6,
    services: [service.BFM],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  StandardAndExtSvc: {
    id: "7",
    bankCode: "9999",
    name: "スタンダードプラン（外部サービス付）",
    price: 7500,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "スタンダードプラン（外部サービス付）の説明",
    displayOrder: 7,
    priceIncludingTax: 7500 * 1.1,
    services: [
      service.DXF,
      service.BFM,
      service.Invoice,
      service.MFCInvoice,
      service.Loan,
      service.ExternalTroubleSupport,
      service.ExternalBusinessSupport,
      service.ExternalBenefitSelect,
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  BFMAndExtSvcInactive: {
    id: "8",
    bankCode: "9999",
    name: "資金管理プラン（外部サービス付）- 過去のプラン",
    price: 1100,
    status: PlanStatus.Inactive, // Inactive means past plan
    publishedAtFrom: monthBeforeToday,
    publishedAtTo: addDays(today, -1),
    description: "資金管理プラン（外部サービス付）- 過去のプランの説明",
    displayOrder: 8,
    priceIncludingTax: 1100 * 1.1,
    services: [
      service.BFM,
      service.MFCInvoice,
      service.Loan,
      service.ExternalTroubleSupport,
      service.ExternalBusinessSupport,
      service.ExternalBenefitSelect,
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  BFMAndExtSvcDraft: {
    id: "9",
    bankCode: "9999",
    name: "資金管理プラン（外部サービス付）- 未来のプラン",
    price: 1100,
    status: PlanStatus.Draft, // Draft means future plan
    publishedAtFrom: addDays(today, 1),
    publishedAtTo: monthAfterToday,
    description: "資金管理プラン（外部サービス付）- 未来のプランの説明",
    displayOrder: 9,
    priceIncludingTax: 1100 * 1.1,
    services: [
      service.BFM,
      service.MFCInvoice,
      service.Loan,
      service.ExternalTroubleSupport,
      service.ExternalBusinessSupport,
      service.ExternalBenefitSelect,
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Empty: {
    id: "10",
    bankCode: "9999",
    name: "空プラン",
    price: 0,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "空プランの説明",
    displayOrder: 10,
    priceIncludingTax: 0 * 1.1,
    services: [],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  DXAndCFMAndPayByCard: {
    id: "101",
    bankCode: "9999",
    name: "まとめてDX化+CFM+PayByCardプラン",
    price: 7500,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "まとめてDX化プランの説明",
    displayOrder: 101,
    priceIncludingTax: 7500 * 1.1,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [
          feature.CFMCashFlowForecast,
          feature.CFMCashFlowStatement,
          feature.CFMTransactionMatching,
        ],
      },
      {
        ...service.Invoice,
        planFeatures: [feature.InvoicePayByCard],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  DXAndCFMForecastAndPayByCard: {
    id: "102",
    bankCode: "9999",
    name: "まとめてDX化+CFMForecastプラン",
    price: 7500,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "まとめてDX化プランの説明",
    displayOrder: 102,
    priceIncludingTax: 7500 * 1.1,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [feature.CFMCashFlowForecast],
      },
      {
        ...service.Invoice,
        planFeatures: [feature.InvoicePayByCard],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  DXAndCFMForecast: {
    id: "103",
    bankCode: "9999",
    name: "まとめてDX化+CFMForecastプラン",
    price: 7500,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "まとめてDX化プランの説明",
    displayOrder: 103,
    priceIncludingTax: 7500 * 1.1,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [feature.CFMCashFlowForecast],
      },
      service.Invoice,
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  WorksAndCFM: {
    id: "104",
    bankCode: "9999",
    name: "ワークス+CFMプラン",
    price: 4500,
    priceIncludingTax: 4500 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "ワークスプランの説明",
    displayOrder: 104,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [
          feature.CFMCashFlowForecast,
          feature.CFMCashFlowStatement,
          feature.CFMTransactionMatching,
        ],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  FeatureTestPlanAllEnabled: {
    id: "105",
    bankCode: "9999",
    name: "FeatureTestAllInプラン",
    price: 4500,
    priceIncludingTax: 4500 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "たくさんのフィーチャーが有効なプラン",
    displayOrder: 105,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [
          feature.CFMCashFlowForecast,
          feature.CFMCashFlowStatement,
          feature.CFMTransactionMatching,
        ],
      },
      {
        ...service.FeatureTest,
        planFeatures: [
          feature.TestFeature1,
          feature.TestFeature2,
          feature.TestFeature3,
          feature.TestFeature4,
          feature.TestFeature5,
        ],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  FeatureTestPlanAllDisabled: {
    id: "106",
    bankCode: "9999",
    name: "FeatureTestAllOutプラン",
    price: 4500,
    priceIncludingTax: 4500 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "たくさんのフィーチャーが無効なプラン",
    displayOrder: 106,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [
          feature.CFMCashFlowForecast,
          feature.CFMCashFlowStatement,
          feature.CFMTransactionMatching,
        ],
      },
      {
        ...service.FeatureTest,
        planFeatures: [],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  FeatureTestPlanAllMixed: {
    id: "107",
    bankCode: "9999",
    name: "FeatureTestMixedプラン",
    price: 4500,
    priceIncludingTax: 4500 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "たくさんのフィーチャーが混在するプラン",
    displayOrder: 107,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [
          feature.CFMCashFlowForecast,
          feature.CFMCashFlowStatement,
          feature.CFMTransactionMatching,
        ],
      },
      {
        ...service.FeatureTest,
        planFeatures: [
          feature.TestFeature1,
          feature.TestFeature3,
          feature.TestFeature5,
        ],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  DXWithNoFeatures: {
    id: "108",
    bankCode: "9999",
    name: "まとめてDX化プラン",
    price: 7500,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "まとめてDX化プランの説明",
    displayOrder: 108,
    priceIncludingTax: 7500 * 1.1,
    services: [
      service.DXF,
      service.BFMWithNoFeatures,
      service.InvoiceWithNoFeatures,
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  EdgeCase: {
    id: "109",
    bankCode: "9999",
    name: "EdgeCaseプラン",
    price: 4500,
    priceIncludingTax: 4500 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description:
      "たくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\nたくさんのフィーチャーが混在するプラン\n",
    displayOrder: 109,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [
          feature.CFMCashFlowForecast,
          feature.CFMCashFlowStatement,
          feature.CFMTransactionMatching,
        ],
      },
      {
        ...service.FeatureTest,
        planFeatures: [
          feature.TestFeature1,
          feature.TestFeature3,
          feature.TestFeature5,
        ],
      },
      service.ExternalBusinessSupport,
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  DXWithInvoicePayByCard: {
    id: "110",
    bankCode: "9999",
    name: "まとめてDX化+InvoicePayByCardプラン",
    price: 7000,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "まとめてDX化プランの説明",
    displayOrder: 110,
    priceIncludingTax: 7000 * 1.1,
    services: [
      service.DXF,
      service.BFM,
      {
        ...service.Invoice,
        planFeatures: [feature.InvoicePayByCard],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  WorksAndCFMV1: {
    id: "111",
    bankCode: "9999",
    name: "ワークス+CFMプランV1",
    price: 4500,
    priceIncludingTax: 4500 * 1.1,
    status: PlanStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "ワークスプランの説明",
    displayOrder: 104,
    services: [
      service.DXF,
      {
        ...service.BFM,
        planFeatures: [
          feature.CFMCashFlowForecast,
          feature.CFMCashFlowStatement,
        ],
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
};

const allPlans = [
  plan.DX,
  plan.Accounting,
  plan.Works,
  plan.BFM,
  plan.Draft,
  plan.Inactive,
  plan.StandardAndExtSvc,
  plan.BFMAndExtSvcInactive,
  plan.BFMAndExtSvcDraft,
  plan.Empty,
  plan.DXAndCFMAndPayByCard,
  plan.DXAndCFMForecastAndPayByCard,
  plan.DXAndCFMForecast,
  plan.WorksAndCFM,
];

const planContract = {
  DX: {
    id: "1",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.DX,
  },
  Accounting: {
    id: "2",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.Accounting,
  },
  Works: {
    id: "3",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.Works,
  },
  BFM: {
    id: "4",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.BFM,
  },
  Draft: {
    id: "5",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.Draft,
  },
  Inactive: {
    id: "6",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.Inactive,
  },
  StandardAndExtSvc: {
    id: "7",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.StandardAndExtSvc,
  },
  BFMAndExtSvcInactive: {
    id: "8",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.BFMAndExtSvcInactive,
  },
  BFMAndExtSvcDraft: {
    id: "9",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.BFMAndExtSvcDraft,
  },
  Empty: {
    id: "10",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.Empty,
  },
  DXAndCFM: {
    id: "101",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.DXAndCFMAndPayByCard,
  },
  DXAndCFMForecastOnly: {
    id: "102",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.DXAndCFMAndPayByCard,
  },
  FeaturesTestPlanAllEnabled: {
    id: "103",
    status: PlanContractStatus.Contracted,
    appliedCoupons: [],
    plan: plan.FeatureTestPlanAllEnabled,
  },
};

const option = {
  PhoneSupport: {
    id: "1",
    bankCode: "9999",
    name: "電話サポート",
    price: 500,
    priceIncludingTax: 500 * 1.1,
    status: OptionStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "電話サポートの説明",
    displayOrder: 0,
    service: null,
    optionTerms: [
      {
        id: "1",
        linkText: "電話サポートの規約　",
        url: "https://example.com/denwa",
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  TrainingSession: {
    id: "2",
    bankCode: "9999",
    name: "勉強会",
    price: 700,
    priceIncludingTax: 700 * 1.1,
    status: OptionStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "勉強会サポートの説明",
    displayOrder: 1,
    service: null,
    optionTerms: [
      {
        id: "2",
        linkText: "勉強会の規約",
        url: "https://example.com/benkyoukai",
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  VeryLongName: {
    id: "3",
    bankCode: "9999",
    name: "２０文字以上のいつでもなんでもどこでもサポート",
    price: 2000,
    priceIncludingTax: 2000 * 1.1,
    status: OptionStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "２０文字以上のいつでもなんでもどこでもサポート説明",
    displayOrder: 2,
    service: null,
    optionTerms: [
      {
        id: "3",
        linkText: "２０文字以上のいつでもなんでもどこでもサポートの規約",
        url: "https://example.com/nagai",
      },
      {
        id: "4",
        linkText: "２０文字以上のいつでもなんでもどこでもサポートの規約#2",
        url: "https://example.com/nagai2",
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Inactive: {
    id: "4",
    bankCode: "9999",
    name: "提供終了のオプション",
    price: 2000,
    priceIncludingTax: 2000 * 1.1,
    status: OptionStatus.Inactive,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "提供終了のオプションの説明",
    displayOrder: 3,
    service: null,
    optionTerms: [
      {
        id: "5",
        linkText: "提供終了のオプションの規約",
        url: "https://example.com/inactive",
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Draft: {
    id: "5",
    bankCode: "9999",
    name: "下書きのオプション",
    price: 2000,
    priceIncludingTax: 2000 * 1.1,
    status: OptionStatus.Active,
    publishedAtFrom: today,
    publishedAtTo: monthAfterToday,
    description: "下書きのオプションの説明",
    displayOrder: 4,
    service: null,
    optionTerms: [
      {
        id: "6",
        linkText: "下書きのオプションの規約",
        url: "https://example.com/draft",
      },
    ],
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
  Inactive2: {
    id: "6",
    bankCode: 1,
    name: "提供完全終了のオプション2",
    price: 2000,
    priceIncludingTax: 2000 * 1.1,
    status: OptionStatus.Inactive,
    publishedAtFrom: twoMonthBeforeToday,
    publishedAtTo: monthBeforeToday,
    description: "提供完全終了のオプションの説明2",
    displayOrder: 6,
    optionTerms: [
      {
        id: "7",
        linkText:
          "ダミーテキスト。\n上記サービスに関して、株式会社マネーフォワードが保持している情報は退会後に削除いたします（ただし、利用者登録情報など個人情報保護法の観点で株式会社マネーフォワードに保存が義務付けられている情報は除きます）。これまでの利用の中で蓄積したデータで、今後必要なものについては別途記録をお取りいただくことをおすすめします。",
        url: "",
      },
    ],
    service: null,
    createdAt: bizBillingBirthday,
    updatedAt: bizBillingBirthday,
  },
};

const allOptions = [
  option.PhoneSupport,
  option.TrainingSession,
  option.VeryLongName,
  option.Inactive,
  option.Draft,
  option.Inactive2,
];

const optionContract = {
  PhoneSupport: {
    id: "1",
    status: OptionContractStatus.Contracted,
    usingDateFrom: monthBeforeToday,
    usingDateTo: null,
    option: option.PhoneSupport,
    optionTerms: [],
  },
  TrainingSession: {
    id: "2",
    status: OptionContractStatus.Contracted,
    usingDateFrom: monthBeforeToday,
    usingDateTo: null,
    option: option.TrainingSession,
    optionTerms: [],
  },
};

const coupon = {
  Relative: {
    id: "1",
    bankCode: "9999",
    code: "ABCDE",
    name: "相対期間割引クーポン",
    price: 1000,
    priceIncludingTax: 1100,
    status: CouponStatus.Active,
    discountType: CouponDiscountType.Relative,
    discountPeriod: 3,
    discountPeriodTo: null,
    availablePeriodFrom: today,
    availablePeriodTo: monthAfterToday,
    updatedBy: "1",
  },
  Absolute: {
    id: "2",
    bankCode: "9999",
    code: "VWXYZ",
    name: "固定期間割引クーポン",
    price: 200,
    priceIncludingTax: 200 * 1.1,
    status: CouponStatus.Active,
    discountType: CouponDiscountType.Absolute,
    discountPeriod: null,
    discountPeriodTo: monthAfterToday,
    availablePeriodFrom: today,
    availablePeriodTo: monthAfterToday,
    updatedBy: "1",
  },
  Endless: {
    id: "3",
    bankCode: "9999",
    code: "UNLIMITED",
    name: "無期限クーポン",
    price: 1200,
    priceIncludingTax: 1200 * 1.1,
    status: CouponStatus.Active,
    discountType: CouponDiscountType.Endless,
    discountPeriod: null,
    discountPeriodTo: null,
    availablePeriodFrom: today,
    availablePeriodTo: null,
    updatedBy: "1",
  },
};

const allCoupons = [coupon.Relative, coupon.Absolute, coupon.Endless];

const admin = {
  admin1: {
    name: "名字 名前",
    userSub: "4649f62d-e90c-4bce-9ec2-81c5e0e6920f",
  },
  admin2: {
    name: "名字2 名前2",
    userSub: "01469db6-7b03-49e3-9820-de9482f4fb6d",
  },
  admin3: {
    name: "名字3 名前3",
    userSub: "ada37bb2-2683-4f5a-b47e-e5559cd1c3ff",
  },
  admin4: {
    name: "田中 太郎",
    userSub: "sub1",
  },
};

const companyBillings = [
  {
    id: "1",
    usingMonth: yearMonthBeforeToday(0),
    requestCount: 2,
    billingOn: monthAfterToday,
    amount: 3000,
    taxAmount: 300,
    status: BillingStatus.InRequest,
    billingDetails: [
      {
        id: "1",
        type: BillingDetailType.Plan,
        content: "経理業務おまかせプラン",
        amount: 4000,
      },
      {
        id: "2",
        type: BillingDetailType.Coupon,
        content: "1000円割引クーポン",
        amount: -1000,
      },
    ],
  },
  {
    id: "2",
    usingMonth: yearMonthBeforeToday(1),
    billingOn: today,
    requestCount: 1,
    amount: 3000,
    taxAmount: 300,
    status: BillingStatus.Unclaimed,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "1000円割引クーポン",
        amount: -1000,
        type: BillingDetailType.Coupon,
      },
    ],
  },
  {
    id: "3",
    usingMonth: yearMonthBeforeToday(2),
    billingOn: monthsBeforeToday(1),
    amount: 3000,
    taxAmount: 300,
    requestCount: 2,
    status: BillingStatus.Ng,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "1000円割引クーポン",
        amount: -1000,
        type: BillingDetailType.Coupon,
      },
    ],
  },
  {
    id: "4",
    usingMonth: yearMonthBeforeToday(3),
    billingOn: monthsBeforeToday(2),
    amount: 3000,
    taxAmount: 300,
    requestCount: 1,
    status: BillingStatus.Ok,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "1000円割引クーポン",
        amount: -1000,
        type: BillingDetailType.Coupon,
      },
    ],
  },
  {
    id: "5",
    usingMonth: yearMonthBeforeToday(4),
    billingOn: monthsBeforeToday(3),
    amount: 3000,
    taxAmount: 300,
    requestCount: 1,
    status: BillingStatus.Ok,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "2000円割引クーポン",
        amount: -2000,
        type: BillingDetailType.Coupon,
      },
    ],
  },
  {
    id: "6",
    usingMonth: "202301",
    billingOn: new Date("2023-02-09"),
    amount: 3200,
    taxAmount: 320,
    requestCount: 1,
    status: BillingStatus.Ok,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "電話サポート",
        amount: 500,
        type: BillingDetailType.Option,
      },
      {
        id: "3",
        content: "1000円割引クーポン",
        amount: -1000,
        type: BillingDetailType.Coupon,
      },
      {
        id: "4",
        content: "勉強会",
        amount: 700,
        type: BillingDetailType.Option,
      },
      {
        id: "5",
        content: "",
        amount: -1000,
        type: null,
      },
    ],
  },
  {
    id: "7",
    usingMonth: "202411",
    billingOn: new Date("2024-12-09"),
    amount: 3200,
    taxAmount: 320,
    requestCount: 1,
    status: BillingStatus.Ok,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "電話サポート",
        amount: 500,
        type: BillingDetailType.Option,
      },
      {
        id: "3",
        content: "1000円割引クーポン",
        amount: -1000,
        type: BillingDetailType.Coupon,
      },
      {
        id: "4",
        content: "勉強会",
        amount: 700,
        type: BillingDetailType.Option,
      },
      {
        id: "5",
        content: "",
        amount: -1000,
        type: null,
      },
    ],
  },
  {
    id: "8",
    usingMonth: "202412",
    billingOn: new Date("2025-01-09"),
    amount: 3200,
    taxAmount: 320,
    requestCount: 1,
    status: BillingStatus.Ok,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "電話サポート",
        amount: 500,
        type: BillingDetailType.Option,
      },
      {
        id: "3",
        content: "1000円割引クーポン",
        amount: -1000,
        type: BillingDetailType.Coupon,
      },
      {
        id: "4",
        content: "勉強会",
        amount: 700,
        type: BillingDetailType.Option,
      },
      {
        id: "5",
        content: "",
        amount: -1000,
        type: null,
      },
    ],
  },
  {
    id: "8",
    usingMonth: "202501",
    billingOn: new Date("2025-02-09"),
    amount: 3200,
    taxAmount: 320,
    requestCount: 1,
    status: BillingStatus.Ok,
    billingDetails: [
      {
        id: "1",
        content: "経理業務おまかせプラン",
        amount: 4000,
        type: BillingDetailType.Plan,
      },
      {
        id: "2",
        content: "電話サポート",
        amount: 500,
        type: BillingDetailType.Option,
      },
      {
        id: "3",
        content: "1000円割引クーポン",
        amount: -1000,
        type: BillingDetailType.Coupon,
      },
      {
        id: "4",
        content: "勉強会",
        amount: 700,
        type: BillingDetailType.Option,
      },
      {
        id: "5",
        content: "",
        amount: -1000,
        type: null,
      },
    ],
  },
];

const companyCanceledOptionContracts = [
  {
    id: "4",
    status: OptionContractStatus.Contracted,
    usingDateFrom: twoMonthBeforeToday,
    usingDateTo: lastDayOfMonth(today),
    option: option.Inactive,
  },
  {
    id: "5",
    status: OptionContractStatus.Contracted,
    usingDateFrom: twoMonthBeforeToday,
    usingDateTo: monthBeforeToday,
    option: option.Inactive2,
  },
];
const companyOptionContracts = [
  {
    id: "1",
    status: OptionContractStatus.ProvisionalContract,
    usingDateFrom: today,
    usingDateTo: monthAfterToday,
    option: option.PhoneSupport,
  },
  {
    id: "2",
    status: OptionContractStatus.Contracted,
    usingDateFrom: today,
    usingDateTo: monthAfterToday,
    option: option.TrainingSession,
  },
  {
    id: "3",
    status: OptionContractStatus.Contracted,
    usingDateFrom: twoMonthBeforeToday,
    usingDateTo: monthAfterToday,
    option: {
      id: "3",
      bankCode: 1,
      name: "テストのオプション",
      price: 2000,
      priceIncludingTax: 2000 * 1.1,
      status: OptionStatus.Inactive,
      publishedAtFrom: twoMonthBeforeToday,
      publishedAtTo: monthAfterToday,
      description: "テストのオプションのオプションの説明",
      displayOrder: 2,
      optionTerms: [
        {
          id: "1",
          linkText: "",
          url: "",
        },
      ],
      service: null,
      createdAt: bizBillingBirthday,
      updatedAt: bizBillingBirthday,
    },
  },
];

const companyPlanContract = {
  id: "5",
  status: PlanContractStatus.Contracted,
  usingDateFrom: subDays(today, 3),
  usingDateTo: null,
  appliedCoupons: [
    {
      id: "1",
      applicatedOnFrom: twoMonthBeforeToday,
      applicatedOnTo: monthAfterToday,
      coupon: coupon.Relative,
    },
  ],
  plan: plan.DXAndCFMAndPayByCard,
};

const companyCancelledPlanContracts = [
  {
    id: "4",
    status: PlanContractStatus.Contracted,
    usingDateFrom: subDays(today, 10),
    usingDateTo: subDays(today, 3),
    appliedCoupons: [
      {
        id: "1",
        applicatedOnFrom: twoMonthBeforeToday,
        applicatedOnTo: monthAfterToday,
        coupon: {
          id: "1",
          bankCode: 1,
          code: "ABCDE",
          name: "固定期間割引クーポン",
          price: 200,
          priceIncludingTax: 200 * 1.1,
          status: CouponStatus.Active,
          discountType: CouponDiscountType.Absolute,
          discountPeriod: 1,
          discountPeriodTo: null,
          availablePeriodFrom: today,
          availablePeriodTo: monthAfterToday,
          updatedBy: "1",
        },
      },
    ],
    plan: plan.StandardAndExtSvc,
  },
  {
    id: "3",
    status: PlanContractStatus.Contracted,
    usingDateFrom: subDays(today, 20),
    usingDateTo: subDays(today, 10),
    appliedCoupons: [
      {
        id: "1",
        applicatedOnFrom: twoMonthBeforeToday,
        applicatedOnTo: monthAfterToday,
        coupon: coupon.Relative,
      },
    ],
    plan: plan.DX,
  },
  {
    id: "2",
    status: PlanContractStatus.Contracted,
    usingDateFrom: subDays(today, 40),
    usingDateTo: subDays(today, 20),
    appliedCoupons: [
      {
        id: "1",
        applicatedOnFrom: twoMonthBeforeToday,
        applicatedOnTo: monthAfterToday,
        coupon: coupon.Relative,
      },
    ],
    plan: plan.FeatureTestPlanAllEnabled,
  },
  {
    id: "1",
    status: PlanContractStatus.Contracted,
    usingDateFrom: subDays(today, 80),
    usingDateTo: subDays(today, 40),
    appliedCoupons: [
      {
        id: "1",
        applicatedOnFrom: twoMonthBeforeToday,
        applicatedOnTo: monthAfterToday,
        coupon: coupon.Relative,
      },
    ],
    plan: plan.DXAndCFMAndPayByCard,
  },
];

const company = {
  uuid: "1",
  status: CompanyStatus.Active,
  cardRegistered: true,
  isFirstMonthOfContract: false,
  currentTotalAmount: 3300,
  admins: [admin.admin1, admin.admin2, admin.admin3, admin.admin4],
  billings: companyBillings,
  planContract: companyPlanContract,
  canceledPlanContracts: companyCancelledPlanContracts,
  optionContracts: companyOptionContracts,
  canceledOptionContracts: companyCanceledOptionContracts,
  availableServices: [
    service.DXF,
    service.BFM,
    service.MFCInvoice,
    service.ExternalTroubleSupport,
    service.ExternalBusinessSupport,
    service.ExternalBenefitSelect,
  ],
  nextMonthBilling: {
    planName: "まとめてDX化プラン",
    planAmount: 7500,
    planTaxAmount: 750,
    billing: {
      usingMonth: "6月",
      billingOn: monthAfterToday,
      amount: 7500,
      taxAmount: 750,
      billingDetails: [],
    },
  },
};

export const ReferenceData = {
  now: today,
  twoMonthsBeforeToday: twoMonthBeforeToday,
  twoYearMonthBeforeToday: yearMonthBeforeToday(2),
  feature: feature,
  service: service,
  plan: plan,
  planContract: planContract,
  coupon: coupon,
  option: option,
  optionContract: optionContract,
  bankServices: bankServices,

  bankServicesQuery: {
    bankServices: {
      bankCode: "9999",
      services: allServices,
      options: allOptions,
      coupons: allCoupons,
      plans: allPlans,
    },
  },
  companyQuery: {
    company: company,
  },
};
